import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../utils/api";
import {
    FaEnvelopeOpen, FaStar, FaClock,
    FaCheckCircle, FaSpinner, FaArrowRight,
} from "react-icons/fa";

const STATUS_STYLES = {
    new: "bg-blue-100 text-blue-700",
    "in-progress": "bg-yellow-100 text-yellow-700",
    resolved: "bg-green-100 text-green-700",
};

function StatCard({ label, value, sub, icon: Icon, color, link }) {
    return (
        <Link to={link} className="block group">
            <div className={`bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all duration-200`}>
                <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
                        <Icon className="text-xl" />
                    </div>
                    <FaArrowRight className="text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                </div>
                <p className="text-3xl font-bold text-slate-800">{value}</p>
                <p className="text-slate-500 text-sm font-medium mt-1">{label}</p>
                {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
            </div>
        </Link>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.getStats().then((res) => {
            setStats(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <FaSpinner className="animate-spin text-blue-600 text-3xl" />
            </div>
        );
    }

    const { enquiries, testimonials, services, recentEnquiries } = stats || {};

    return (
        <div>
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
                <p className="text-slate-500 text-sm mt-1">Welcome back! Here's what's happening with your site.</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
                <StatCard
                    label="New Enquiries"
                    value={enquiries?.new ?? 0}
                    sub={`${enquiries?.total ?? 0} total`}
                    icon={FaEnvelopeOpen}
                    color="bg-blue-50 text-blue-600"
                    link="/admin/enquiries"
                />
                <StatCard
                    label="In Progress"
                    value={enquiries?.inProgress ?? 0}
                    sub="Being handled"
                    icon={FaClock}
                    color="bg-yellow-50 text-yellow-600"
                    link="/admin/enquiries?status=in-progress"
                />
                <StatCard
                    label="Resolved"
                    value={enquiries?.resolved ?? 0}
                    sub="Completed cases"
                    icon={FaCheckCircle}
                    color="bg-green-50 text-green-600"
                    link="/admin/enquiries?status=resolved"
                />
                <StatCard
                    label="Testimonials"
                    value={testimonials ?? 0}
                    sub={`${services ?? 0} active services`}
                    icon={FaStar}
                    color="bg-purple-50 text-purple-600"
                    link="/admin/testimonials"
                />
            </div>

            {/* Recent Enquiries */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
                    <h2 className="font-bold text-slate-800">Recent Enquiries</h2>
                    <Link to="/admin/enquiries" className="text-blue-600 text-sm font-medium hover:underline flex items-center gap-1">
                        View all <FaArrowRight className="text-xs" />
                    </Link>
                </div>

                <div className="divide-y divide-slate-50">
                    {recentEnquiries?.length === 0 && (
                        <p className="text-center text-slate-400 py-10 text-sm">No enquiries yet.</p>
                    )}
                    {recentEnquiries?.map((e) => (
                        <div key={e._id} className="flex items-center px-6 py-4 hover:bg-slate-50 transition">
                            <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 mr-4">
                                {e.name?.[0]?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-800 text-sm truncate">{e.name}</p>
                                <p className="text-slate-400 text-xs truncate">{e.email} · {e.visaType || "General"}</p>
                            </div>
                            <div className="flex items-center gap-3 ml-4">
                                <span className={`text-xs font-semibold px-3 py-1 rounded-full capitalize ${STATUS_STYLES[e.status] || "bg-slate-100 text-slate-500"}`}>
                                    {e.status}
                                </span>
                                <span className="text-slate-400 text-xs hidden sm:block">
                                    {new Date(e.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
